import { getProductTitleFromProductType, ProductTypeLabelsUnion } from '../../../../shared/helpers/sitecore';
import { Offer, Price } from '../../../../shared/types';

/**
 * Builds the card subtitle from the product types present in the offer: the CMS label for each
 * product type, deduplicated, joined with " & " and lower-cased (e.g. "stroom & gas").
 */
export const getOfferSubtitle = (offer: Offer, productTypeLabels?: ProductTypeLabelsUnion): string => {
  const labels = (offer.products ?? []).map(product =>
    getProductTitleFromProductType(product.type, productTypeLabels)?.toLocaleLowerCase(),
  );

  return [...new Set(labels)].join(' & ').toLowerCase();
};

export interface OfferPrices {
  /** Headline price the customer effectively pays per month, including any discount. */
  headline: Price;
  /** The undiscounted monthly term amount ("Termijnbedrag"). Only present when the offer has a discount. */
  secondary?: Price;
}

const HEADLINE_PERIOD_WITH_DISCOUNT = 'Per maand inclusief korting';
const HEADLINE_PERIOD = 'Per maand';
const SECONDARY_PERIOD = 'Termijnbedrag';

/**
 * Resolves the dual price shown on the experiment product card.
 *
 * When the offer carries a discounted monthly cost (`costsMonthlyWithCashback`, sourced from the
 * backend `monthlyCostsWithCashBack` cost detail), that value becomes the discounted headline
 * ("Per maand inclusief korting") and the regular monthly term amount (`costsMonthly`) is shown as
 * the secondary "Termijnbedrag". Without a discount only a single "Per maand" price is shown.
 */
export const getOfferPrices = (offer: Offer): OfferPrices => {
  const termijnbedrag = offer.costsMonthly?.vatIncluded ?? 0;
  const discountedMonthly = offer.costsMonthlyWithCashback?.vatIncluded;

  if (discountedMonthly == null) {
    return {
      headline: { value: termijnbedrag, period: HEADLINE_PERIOD },
    };
  }

  return {
    headline: { value: discountedMonthly, period: HEADLINE_PERIOD_WITH_DISCOUNT },
    secondary: { value: termijnbedrag, period: SECONDARY_PERIOD },
  };
};

/**
 * Resolves the single, undiscounted price shown on the variant C product card: always the regular
 * monthly term amount (`costsMonthly`) labelled "Per maand", regardless of any discount on the
 * offer. Variant C is identical to variant B but never surfaces the discounted "inclusief korting"
 * headline or the secondary "Termijnbedrag".
 */
export const getOfferPriceWithoutDiscount = (offer: Offer): Price => ({
  value: offer.costsMonthly?.vatIncluded ?? 0,
  period: HEADLINE_PERIOD,
});
